// Admin › Help › Articles: every help article, its newest revision and what
// needs a look, with New article. The queue ("Needs a look") is the first view.
// docs/wip/SCOUT_HELP.md

import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  FIRST_SECTIONS,
  STATE_LABELS,
  TOPIC_ID,
  TYPE_LABELS,
  errorText,
  toSlug,
  useAddFirstSections,
  useCreateArticle,
  useHelpArticles,
  useHelpSections,
  type ArticleType,
  type HelpArticleRow,
  type HelpSection,
  type RevisionState,
} from './data/help';

type Filter = 'look' | 'ready' | 'draft' | 'all';
const FILTERS: { key: Filter; label: string; test: (a: HelpArticleRow) => boolean }[] = [
  { key: 'look', label: 'Needs a look', test: (a) => a.openFlags > 0 },
  { key: 'ready', label: 'Ready for check', test: (a) => a.latest.state === 'ready' },
  { key: 'draft', label: 'Drafts', test: (a) => a.latest.state === 'draft' },
  { key: 'all', label: 'All', test: () => true },
];

const STATE_STYLES: Record<RevisionState, string> = {
  draft: 'bg-slate-400/15 text-slate-200',
  ready: 'bg-cyan-400/15 text-cyan-200',
  checked: 'bg-blue-400/15 text-blue-200',
  published: 'bg-emerald-400/15 text-emerald-200',
};

const field = 'min-h-[44px] w-full rounded-[10px] border border-slate-400/20 bg-slate-900/60 px-3 text-[15px] text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40';
const label = 'flex flex-col gap-1.5 text-sm font-medium text-slate-300';

export function HelpArticlesPage() {
  const articles = useHelpArticles();
  const sections = useHelpSections();
  const [filter, setFilter] = useState<Filter>('look');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const list = useMemo(() => articles.data ?? [], [articles.data]);
  const sectionList = useMemo(() => sections.data ?? [], [sections.data]);
  const sectionOf = (id: string) => sectionList.find((s) => s.id === id);
  const q = query.trim().toLowerCase();
  const shown = list
    .filter(FILTERS.find((f) => f.key === filter)!.test)
    .filter((a) => !q || a.latest.title.toLowerCase().includes(q) || a.topicId.toLowerCase().includes(q));
  const live = list.filter((a) => a.liveNumber !== null).length;

  return (
    <div className="max-w-[1120px] space-y-7 px-4 pb-16 pt-6 sm:px-6 lg:px-10 lg:pt-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="text-[26px] font-bold leading-none tracking-tight text-white">Articles</h1>
          <p className="text-[15px] text-slate-400">
            {live ? `${live} live` : 'Nothing published yet'} · {list.length} article{list.length === 1 ? '' : 's'}
          </p>
        </div>
        {sectionList.length > 0 && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex min-h-[44px] items-center rounded-[10px] border border-slate-400/25 px-4 text-[15px] font-semibold text-white hover:bg-slate-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
          >
            New article
          </button>
        )}
      </div>

      {sections.isSuccess && sectionList.length === 0 && <NoSections sections={sectionList} />}
      {adding && <NewArticle sections={sectionList} onCreated={() => { setAdding(false); setFilter('draft'); }} onCancel={() => setAdding(false)} />}

      {articles.isLoading && <p className="text-[15px] text-slate-400">Loading…</p>}
      {articles.isError && <p className="text-[15px] text-red-400">Couldn't load the articles.</p>}

      {articles.isSuccess && list.length === 0 && (
        <div className="rounded-2xl border border-slate-400/15 bg-slate-800/45 px-6 py-10 text-center">
          <p className="text-base font-semibold text-white">No articles yet</p>
          {sectionList.length > 0 && <p className="mt-1.5 text-[15px] text-slate-400">Start one with New article.</p>}
        </div>
      )}

      {articles.isSuccess && list.length > 0 && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="tablist" aria-label="Show" className="flex flex-wrap gap-0.5 rounded-[10px] bg-slate-400/10 p-[3px]">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={`min-h-[36px] rounded-[7px] px-3 text-[15px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
                    filter === f.key ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {f.label} <span className={filter === f.key ? 'text-slate-400' : 'text-slate-500'}>{list.filter(f.test).length}</span>
                </button>
              ))}
            </div>
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">Search articles</span>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" aria-hidden="true" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Title or topic ID" className={`${field} pl-10`} />
            </label>
          </div>

          {shown.length === 0 ? (
            <div className="rounded-2xl border border-slate-400/15 bg-slate-800/45 px-6 py-10 text-center">
              <p className="text-base font-semibold text-white">
                {q ? 'No article matches' : filter === 'look' ? 'Nothing needs a look' : 'None here'}
              </p>
            </div>
          ) : (
            <>
            {/* Phones: a list, as the mock's phone board shows; wider screens: the table. */}
            <ul className="overflow-hidden rounded-2xl border border-slate-400/15 bg-slate-800/45 sm:hidden">
              {shown.map((a) => (
                <li key={a.id} className="space-y-1 border-t border-slate-400/10 px-4 py-3.5 first:border-t-0">
                  <span className="block font-mono text-[13px] text-slate-400">{a.topicId} · {sectionOf(a.sectionId)?.title ?? '—'}</span>
                  <Link to={`/admin/help/articles/${a.id}`} className="block text-[15px] font-semibold text-slate-100 hover:text-cyan-200">{a.latest.title}</Link>
                  {a.openFlags > 0
                    ? <span className="block text-[13px] text-amber-300">{a.openFlags} thing{a.openFlags === 1 ? '' : 's'} to look at</span>
                    : <StateChip state={a.latest.state} />}
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-2xl border border-slate-400/15 bg-slate-800/45 sm:block">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="text-[13px] font-semibold text-slate-400">
                    <th scope="col" className="px-5 py-3 font-semibold">Topic</th>
                    <th scope="col" className="px-3 py-3 font-semibold">Article</th>
                    <th scope="col" className="px-3 py-3 font-semibold">Section</th>
                    <th scope="col" className="px-5 py-3 font-semibold">State</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((a) => (
                    <tr key={a.id} className="border-t border-slate-400/10 align-top">
                      <td className="px-5 py-3.5 font-mono text-sm text-slate-400">{a.topicId}</td>
                      <td className="px-3 py-3.5">
                        <Link to={`/admin/help/articles/${a.id}`} className="block text-[15px] font-semibold text-slate-100 hover:text-cyan-200">{a.latest.title}</Link>
                        <span className="block text-[13px] text-slate-400">
                          {TYPE_LABELS[a.type]} · revision {a.latest.number} by {a.latest.authorName}
                          {a.liveNumber !== null && a.liveNumber !== a.latest.number ? ` · revision ${a.liveNumber} is live` : ''}
                        </span>
                        {a.openFlags > 0 && (
                          <span className="block text-[13px] text-amber-300">{a.openFlags} thing{a.openFlags === 1 ? '' : 's'} to look at</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-[15px] text-slate-300">{sectionOf(a.sectionId)?.title ?? '—'}</td>
                      <td className="px-5 py-3.5"><StateChip state={a.latest.state} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function StateChip({ state }: { state: RevisionState }) {
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[13px] font-semibold ${STATE_STYLES[state]}`}>{STATE_LABELS[state]}</span>;
}

function NoSections({ sections }: { sections: HelpSection[] }) {
  const add = useAddFirstSections();
  return (
    <div className="space-y-4 rounded-2xl border border-slate-400/15 bg-slate-800/45 px-6 py-6">
      <div className="space-y-1.5">
        <p className="text-base font-semibold text-white">No sections yet</p>
        <p className="text-[15px] text-slate-400">Articles belong to a section. These are the spec's first cut, in the order the Scout lists them:</p>
        <p className="text-[15px] text-slate-300">{FIRST_SECTIONS.map((s) => s.title).join(' · ')}</p>
      </div>
      <button
        type="button"
        disabled={add.isPending}
        onClick={() => add.mutate(sections)}
        className="inline-flex min-h-[44px] items-center rounded-[10px] bg-cyan-600 px-4 text-[15px] font-semibold text-white hover:bg-cyan-500 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        {add.isPending ? 'Adding…' : 'Add these sections'}
      </button>
      {add.isError && <p className="text-[15px] text-red-400">{errorText(add.error)}</p>}
    </div>
  );
}

function NewArticle({ sections, onCreated, onCancel }: { sections: HelpSection[]; onCreated: () => void; onCancel: () => void }) {
  const create = useCreateArticle();
  const [title, setTitle] = useState('');
  const [topicId, setTopicId] = useState('');
  const [sectionId, setSectionId] = useState(sections[0]?.id ?? '');
  const [type, setType] = useState<ArticleType>('task');
  const [summary, setSummary] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const address = slug ?? toSlug(title);
  const section = sections.find((s) => s.id === sectionId);
  const topicOk = TOPIC_ID.test(topicId);
  const ready = title.trim() && topicOk && sectionId && summary.trim() && summary.length <= 160 && address;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    create.mutate(
      { topicId, sectionId, slug: address, type, revision: { title: title.trim(), summary: summary.trim(), body: [] } },
      { onSuccess: onCreated },
    );
  };

  return (
    <form onSubmit={submit} aria-label="New article" className="space-y-5 rounded-2xl border border-cyan-400/25 bg-slate-800/60 px-6 py-6">
      <h2 className="text-[19px] font-semibold text-white">New article</h2>
      <div className="grid gap-5 sm:grid-cols-2">
        <label className={label}>
          Title
          <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Set an anchor watch" required />
        </label>
        <label className={label}>
          Topic ID
          <input
            className={`${field} font-mono`}
            value={topicId}
            onChange={(e) => setTopicId(e.target.value.toUpperCase())}
            placeholder="ANC-01"
            aria-describedby="topic-hint"
            required
          />
          <span id="topic-hint" className={`text-[13px] font-normal ${topicId && !topicOk ? 'text-amber-300' : 'text-slate-500'}`}>
            Letters, a dash and two or three digits. Permanent: never reused.
          </span>
        </label>
        <label className={label}>
          Section
          <select className={field} value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </label>
        <label className={label}>
          Type
          <select className={field} value={type} onChange={(e) => setType(e.target.value as ArticleType)}>
            {(Object.keys(TYPE_LABELS) as ArticleType[]).map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </label>
      </div>
      <label className={label}>
        <span className="flex justify-between">
          Summary <span className={`font-normal ${summary.length > 160 ? 'text-amber-300' : 'text-slate-500'}`}>{summary.length} / 160</span>
        </span>
        <textarea className={`${field} min-h-[72px] py-2.5`} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="One sentence, shown in lists and search." required />
      </label>
      <label className={label}>
        Address
        <span className="flex flex-wrap items-center gap-1 font-mono text-[15px] font-normal text-slate-400">
          <span className="shrink-0 whitespace-nowrap">/{section?.slug ?? ''}/</span>
          <input className={`${field} min-w-[12rem] flex-1 font-mono`} value={address} onChange={(e) => setSlug(toSlug(e.target.value))} aria-label="Address" />
        </span>
      </label>
      {create.isError && <p className="text-[15px] text-red-400">{errorText(create.error)}</p>}
      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={!ready || create.isPending}
          className="inline-flex min-h-[44px] items-center rounded-[10px] bg-cyan-600 px-4 text-[15px] font-semibold text-white hover:bg-cyan-500 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {create.isPending ? 'Creating…' : 'Create the draft'}
        </button>
        <button type="button" onClick={onCancel} className="inline-flex min-h-[44px] items-center rounded-[10px] px-4 text-[15px] font-medium text-slate-300 hover:bg-slate-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
          Cancel
        </button>
      </div>
    </form>
  );
}
